/**
 * Mark someone out of office — the write half of `crm.user_absences`.
 *
 * This is NOT a calendar event. Personal calendar events are owner-private and
 * nobody can write another user's; an absence is deliberately TEAM-VISIBLE and
 * anyone on the CRM may file one for themselves or for a teammate (the owner's
 * explicit ask: "a user can mark another user out of office"). Every write
 * records who filed it.
 *
 * What it actually does, in the product's words: nothing is reassigned. While
 * the days are live, that person's conversations, callbacks, follow-ups and
 * tasks are SHARED OUT among the other recruiters on their studies who are in —
 * each item landing with exactly one of them — and the morning the absence ends
 * everything silently returns. The modal says that out loud, because a person
 * clicking "mark Dana out" is entitled to know what happens to Dana's desk.
 *
 * Opened on an existing absence it becomes the read + cancel view instead.
 * Cancelling is soft on the server (cancelled_at/cancelled_by), so the record of
 * who undid it survives.
 */
import { useMemo, useState } from 'react';
import { CalendarOff, Loader2, UserMinus, X } from 'lucide-react';
import api from '../../../services/api';
import { useAuth } from '../../../contexts/AuthContext';
import { AbsenceKind, UserAbsence } from '../../types';
import { invalidateTeam, useIsCRMAdmin, useTeam } from '../../lib';
import { Alert, Z, btnDanger, btnGhost, btnPrimary } from '../ui';

const errMsg = (e: any): string =>
  e?.response?.data?.detail || e?.response?.data?.error || e?.message || 'Request failed';

const pad = (n: number) => String(n).padStart(2, '0');

/** Local calendar day as YYYY-MM-DD (the shape `starts_on`/`ends_on` use). */
export const toDayKey = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

/**
 * A YYYY-MM-DD day back to a local Date. Built from the parts, never
 * `new Date('2026-08-21')` — that parses as UTC midnight and lands on the
 * previous day for anyone west of Greenwich.
 */
export function dayKeyToDate(key: string): Date {
  const [y, m, d] = key.split('-').map(Number);
  return new Date(y || 1970, (m || 1) - 1, d || 1);
}

/** "Thu, Aug 21" (adds the year only when it isn't this one). */
export function shortDay(key: string): string {
  const d = dayKeyToDate(key);
  const sameYear = d.getFullYear() === new Date().getFullYear();
  return d.toLocaleDateString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    ...(sameYear ? {} : { year: 'numeric' }),
  });
}

/** The day after an absence's last day — "back Mon, Aug 25". */
export function backOnLabel(endsOn: string | null | undefined): string | null {
  if (!endsOn) return null;
  const d = dayKeyToDate(endsOn);
  d.setDate(d.getDate() + 1);
  return shortDay(toDayKey(d));
}

/** Inclusive day count across two YYYY-MM-DD days (1 for a single day). */
export function dayCount(from: string, to: string): number {
  const a = dayKeyToDate(from).getTime();
  const b = dayKeyToDate(to).getTime();
  return Math.max(1, Math.round((b - a) / 86_400_000) + 1);
}

export const ABSENCE_KINDS: { value: AbsenceKind; label: string }[] = [
  { value: 'out', label: 'Out of office' },
  { value: 'pto', label: 'PTO / vacation' },
  { value: 'sick', label: 'Sick' },
  { value: 'training', label: 'Training / conference' },
  { value: 'other', label: 'Other' },
];

export function absenceKindLabel(kind: AbsenceKind | string | null | undefined): string {
  return ABSENCE_KINDS.find((k) => k.value === kind)?.label || 'Out of office';
}

const firstName = (n: string | null | undefined) => (n || '').trim().split(/\s+/)[0] || '';

/** "Dana's" / "Chris'" — good enough for a sentence of UI copy. */
const possessive = (n: string) => (n.endsWith('s') ? `${n}'` : `${n}'s`);

interface AbsenceModalProps {
  /** Present → read + cancel that absence. Absent → the create form. */
  absence?: UserAbsence | null;
  /** Create-form prefills. */
  initialUserId?: string | null;
  initialStart?: string;
  initialEnd?: string;
  /**
   * Links the absence to the personal calendar event that spawned it, so the
   * event and the absence stay recognisably the same act of PTO.
   */
  calendarEventId?: string | null;
  onClose: () => void;
  /** Parent refetches. Receives the created absence, or null on a cancel. */
  onSaved: (absence: UserAbsence | null) => void;
}

export default function AbsenceModal({
  absence = null,
  initialUserId = null,
  initialStart,
  initialEnd,
  calendarEventId = null,
  onClose,
  onSaved,
}: AbsenceModalProps) {
  const { user } = useAuth();
  const isAdmin = useIsCRMAdmin();
  const { team } = useTeam();
  const meId = user?.id || '';
  const viewing = !!absence;

  const today = toDayKey(new Date());
  const [userId, setUserId] = useState<string>(initialUserId || meId);
  const [startsOn, setStartsOn] = useState<string>(initialStart || today);
  const [endsOn, setEndsOn] = useState<string>(initialEnd || initialStart || today);
  const [kind, setKind] = useState<AbsenceKind>('out');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Your own name first, then everyone else alphabetically — the same order the
  // calendar's recruiter picker uses.
  const ordered = useMemo(() => {
    const me = team.filter((m) => m.id === meId);
    const rest = team
      .filter((m) => m.id !== meId)
      .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    return [...me, ...rest];
  }, [team, meId]);

  const subjectName = viewing
    ? absence!.user_name || 'This recruiter'
    : team.find((m) => m.id === userId)?.name || 'This recruiter';
  const isSelf = (viewing ? absence!.user_id : userId) === meId;
  const who = isSelf ? 'Your' : possessive(firstName(subjectName) || subjectName);
  const their = isSelf ? 'your' : 'their';
  const theyAre = isSelf ? "you're" : "they're";

  const rangeInvalid = !viewing && !!startsOn && !!endsOn && endsOn < startsOn;

  const save = async () => {
    setError(null);
    if (!startsOn || !endsOn) {
      setError('Pick the first and last day away');
      return;
    }
    if (rangeInvalid) {
      setError('The last day away must be on or after the first');
      return;
    }
    setSaving(true);
    try {
      const res = await api.post('/api/crm/absences', {
        // Omitting user_id means "me" on the server; send it only when it is
        // somebody else, so the self case takes the plainest path.
        ...(userId && userId !== meId ? { user_id: userId } : {}),
        starts_on: startsOn,
        ends_on: endsOn,
        kind,
        note: note.trim() || null,
        ...(calendarEventId ? { calendar_event_id: calendarEventId } : {}),
      });
      // /api/crm/team carries out_today / out_until and is cached for the whole
      // session — without this, every owner picker keeps offering this person as
      // available until the tab reloads.
      invalidateTeam();
      onSaved((res.data?.absence as UserAbsence) || null);
      onClose();
    } catch (e: any) {
      setError(errMsg(e));
      setSaving(false);
    }
  };

  const cancelAbsence = async () => {
    if (!absence) return;
    setError(null);
    setCancelling(true);
    try {
      await api.delete(`/api/crm/absences/${absence.id}`);
      invalidateTeam();   // they are available again — refresh the cached team
      onSaved(null);
      onClose();
    } catch (e: any) {
      setError(errMsg(e));
      setCancelling(false);
      setConfirmCancel(false);
    }
  };

  // The server is the authority (subject, filer, or CRM admin); this only keeps
  // the button from being offered to someone it would refuse.
  const mayCancel =
    !!absence && (absence.user_id === meId || absence.created_by === meId || isAdmin);

  const busy = saving || cancelling;
  const inputCls =
    'w-full rounded-lg border border-gray-300 bg-white px-2.5 py-1.5 text-sm text-gray-900 transition-colors duration-150 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100';
  const labelCls = 'mb-1 block text-xs font-medium text-gray-600 dark:text-gray-300';

  const rangeLabel = viewing
    ? absence!.starts_on === absence!.ends_on
      ? shortDay(absence!.starts_on)
      : `${shortDay(absence!.starts_on)} – ${shortDay(absence!.ends_on)}`
    : startsOn === endsOn
    ? shortDay(startsOn)
    : `${shortDay(startsOn)} – ${shortDay(endsOn)}`;

  const days = viewing
    ? dayCount(absence!.starts_on, absence!.ends_on)
    : rangeInvalid
    ? 0
    : dayCount(startsOn, endsOn);

  return (
    <div
      className={`fixed inset-0 ${Z.modalRaised} flex items-center justify-center bg-black/50 p-4`}
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={viewing ? 'Out of office' : 'Mark out of office'}
        className="max-h-[90vh] w-full max-w-md overflow-y-auto rounded-xl bg-white p-5 shadow-xl dark:bg-gray-800"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-center justify-between">
          <h3 className="flex items-center gap-2 text-base font-semibold text-gray-900 dark:text-gray-100">
            <UserMinus className="h-4 w-4 text-primary-600 dark:text-primary-400" />
            {viewing ? 'Out of office' : 'Mark out of office'}
          </h3>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="rounded-lg p-1 text-gray-400 transition-colors duration-150 hover:bg-gray-100 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:hover:bg-gray-700"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {viewing ? (
          <div className="space-y-3">
            <div className="rounded-lg border border-gray-200 p-3 dark:border-gray-700">
              <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                {absence!.user_name || 'Recruiter'}
                {isSelf && <span className="ml-1 text-xs font-normal text-gray-400">(you)</span>}
              </div>
              <div className="mt-0.5 text-sm text-gray-600 dark:text-gray-300">
                {rangeLabel}
                <span className="text-gray-400"> · {days === 1 ? '1 day' : `${days} days`}</span>
              </div>
              <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                {absenceKindLabel(absence!.kind)}
                {backOnLabel(absence!.ends_on) && <> · back {backOnLabel(absence!.ends_on)}</>}
              </div>
              {absence!.note && (
                <p className="mt-2 whitespace-pre-wrap text-sm text-gray-700 dark:text-gray-200">{absence!.note}</p>
              )}
              {absence!.created_by && absence!.created_by !== absence!.user_id && (
                <p className="mt-2 text-xs text-gray-400">
                  Marked out by {absence!.created_by_name || 'a teammate'}
                </p>
              )}
            </div>

            <Alert tone="info" size="sm">
              {who} conversations, callbacks, follow-ups and tasks for these days are shared out among the other
              recruiters on {their} studies who are in. Nothing was reassigned — it all comes back the morning{' '}
              {theyAre} back.
            </Alert>

            {error && (
              <Alert tone="error" size="sm">
                {error}
              </Alert>
            )}

            {!mayCancel && (
              <p className="text-xs text-gray-400">
                Only {firstName(subjectName) || 'this recruiter'}, whoever marked them out, or a CRM admin can cancel
                this.
              </p>
            )}

            <div className="mt-5 flex items-center justify-between gap-2">
              {mayCancel ? (
                confirmCancel ? (
                  <button type="button" onClick={cancelAbsence} disabled={busy} className={btnDanger}>
                    {cancelling ? <Loader2 className="h-4 w-4 animate-spin" /> : <CalendarOff className="h-4 w-4" />}
                    Confirm — they're in after all
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => setConfirmCancel(true)}
                    disabled={busy}
                    className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium text-red-600 transition-colors duration-150 hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50 dark:text-red-400 dark:hover:bg-red-900/20 sm:min-h-0"
                  >
                    <CalendarOff className="h-4 w-4" />
                    Cancel this
                  </button>
                )
              ) : (
                <span />
              )}
              <button type="button" onClick={onClose} disabled={busy} className={btnGhost}>
                Close
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <div>
              <label className={labelCls} htmlFor="abs-who">
                Who's out
              </label>
              <select
                id="abs-who"
                value={userId}
                onChange={(e) => setUserId(e.target.value)}
                className={inputCls}
              >
                {!ordered.length && <option value={meId}>Me</option>}
                {ordered.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.name}
                    {m.id === meId ? ' (you)' : ''}
                  </option>
                ))}
              </select>
            </div>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div>
                <label className={labelCls} htmlFor="abs-start">
                  First day away
                </label>
                <input
                  id="abs-start"
                  type="date"
                  value={startsOn}
                  onChange={(e) => {
                    const v = e.target.value;
                    setStartsOn(v);
                    // Keep it a single day unless the user widens it on purpose.
                    if (!endsOn || endsOn < v) setEndsOn(v);
                  }}
                  className={inputCls}
                />
              </div>
              <div>
                <label className={labelCls} htmlFor="abs-end">
                  Last day away
                </label>
                <input
                  id="abs-end"
                  type="date"
                  value={endsOn}
                  min={startsOn || undefined}
                  onChange={(e) => setEndsOn(e.target.value)}
                  className={inputCls}
                />
                <p className="mt-1 text-xs text-gray-400">
                  Both days included{days ? ` · ${days === 1 ? '1 day' : `${days} days`}` : ''}.
                </p>
              </div>
            </div>

            <div>
              <label className={labelCls} htmlFor="abs-kind">
                Reason
              </label>
              <select
                id="abs-kind"
                value={kind}
                onChange={(e) => setKind(e.target.value as AbsenceKind)}
                className={inputCls}
              >
                {ABSENCE_KINDS.map((k) => (
                  <option key={k.value} value={k.value}>
                    {k.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className={labelCls} htmlFor="abs-note">
                Note <span className="text-gray-400">(optional, the team can see it)</span>
              </label>
              <textarea
                id="abs-note"
                value={note}
                maxLength={500}
                rows={2}
                onChange={(e) => setNote(e.target.value)}
                placeholder="e.g. reachable by text for anything urgent"
                className={inputCls}
              />
            </div>

            {/* Say plainly what marking someone out actually does. */}
            <Alert tone="info" size="sm" title="What happens on these days">
              {who} conversations, callbacks, follow-ups and tasks for these days will be shared out among the other
              recruiters on {their} studies who are in, and new leads won't be routed to {isSelf ? 'you' : 'them'}.
              Nothing is reassigned — it all comes back the morning {theyAre} back.
            </Alert>

            {error && (
              <Alert tone="error" size="sm">
                {error}
              </Alert>
            )}

            <div className="mt-5 flex items-center justify-end gap-2">
              <button type="button" onClick={onClose} disabled={busy} className={btnGhost}>
                Cancel
              </button>
              <button type="button" onClick={save} disabled={busy || rangeInvalid} className={btnPrimary}>
                {saving && <Loader2 className="h-4 w-4 animate-spin" />}
                {isSelf ? 'Mark me out' : `Mark ${firstName(subjectName) || 'them'} out`}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
